import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';

const formatDate = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return x.toISOString().slice(0, 10);
};

const PreAuctionCatalogue = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(10);
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [catalogueNo, setCatalogueNo] = useState('');
  const [catalogueDate, setCatalogueDate] = useState(formatDate(new Date()));
  const [auctionDate, setAuctionDate] = useState('');
  const [auctioneerName, setAuctioneerName] = useState('');
  const [auctioneerAddress, setAuctioneerAddress] = useState('');
  const [created, setCreated] = useState(null);

  const fetchPledges = async () => {
    if (!fromDate || !toDate) {
      setError('From date and To date are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseURL}/auction/catalogue/pledges`, {
        params: { fromDate, toDate, limit },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPledges(res.data.data || []);
      setSuccess('');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setPledges([]);
    } finally {
      setLoading(false);
    }
  };

  const createCatalogue = async () => {
    const selectedIds = pledges.map((p) => p._id);
    if (!catalogueNo || !catalogueDate || !auctionDate || selectedIds.length === 0) {
      setError('Catalogue No, Catalogue Date, Auction Date and at least one pledge are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${baseURL}/auction/catalogue`,
        {
          catalogueNo: catalogueNo.trim(),
          catalogueDate,
          auctionDate,
          fromDate,
          toDate,
          pledgeIds: selectedIds,
          auctioneerName: auctioneerName || undefined,
          auctioneerAddress: auctioneerAddress || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreated(res.data.data);
      setSuccess('Catalogue created successfully. You can print from Catalogue list.');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Create Pre-Auction Catalogue</Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField label="From Date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
        <TextField label="To Date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
        <TextField label="Number of articles" type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value) || 10)} inputProps={{ min: 1, max: 500 }} size="small" sx={{ width: 140 }} />
        <Button variant="contained" onClick={fetchPledges} disabled={loading}>Load Pledges</Button>
      </Box>

      {pledges.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>First {pledges.length} pledges (by pledge date) – will be included in catalogue</Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 280, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Pledge Date</TableCell>
                  <TableCell>Ticket No</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Principal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pledges.map((p) => (
                  <TableRow key={p._id}>
                    <TableCell>{formatDate(p.pledgeDate)}</TableCell>
                    <TableCell>{p.ticketNo}</TableCell>
                    <TableCell>{p.customerName}</TableCell>
                    <TableCell>{p.principalAmount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField label="Catalogue No" value={catalogueNo} onChange={(e) => setCatalogueNo(e.target.value)} size="small" placeholder="e.g. 1/2026" />
            <TextField label="Catalogue Date" type="date" value={catalogueDate} onChange={(e) => setCatalogueDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            <TextField label="Auction Date" type="date" value={auctionDate} onChange={(e) => setAuctionDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            <TextField label="Auctioneer Name" value={auctioneerName} onChange={(e) => setAuctioneerName(e.target.value)} size="small" />
            <TextField label="Auctioneer Address" value={auctioneerAddress} onChange={(e) => setAuctioneerAddress(e.target.value)} size="small" multiline maxRows={2} sx={{ minWidth: 200 }} />
          </Box>
          <Button variant="contained" color="primary" onClick={createCatalogue} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Catalogue'}
          </Button>
        </>
      )}
      {created && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Created: {created.catalogueNo} – Auction date: {formatDate(created.auctionDate)}
        </Typography>
      )}
    </Box>
  );
};

export default PreAuctionCatalogue;
